import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLayerOpacity, setLayerVis } from '../../store/actions/layers';
import { layerOpacitySelector, layerVisSelector } from '../../store/selectors/layers';

export const useLayerManager = () => {
  const [layers, setLayers] = useState({});
  const [layerVisibility, setLayerVisibility] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const layerVisInfo = useSelector(layerVisSelector);
  const layerOpacityInfo = useSelector(layerOpacitySelector);
  const layerVisInfoRef = useRef(layerVisInfo);

  useEffect(() => {
    layerVisInfoRef.current = layerVisInfo;
  }, [layerVisInfo]);

  const updateLayer = useCallback((layerId, data) => {
    setLayers(prev => ({ ...prev, [layerId]: data }));
    setLayerVisibility(prev => {
      // Only set visibility if it doesn't already exist in state
      if (prev[layerId] !== undefined) {
        return prev;
      }
      // Initialize with Redux state or false
      return { ...prev, [layerId]: layerVisInfoRef.current[layerId] || false };
    });
  }, []);

  const setLayerLoading = useCallback((layerId, loading) => {
    setLoadingStates(prev => ({ ...prev, [layerId]: loading }));
  }, []);

  const setLayerError = useCallback((layerId, error) => {
    setErrors(prev => ({ ...prev, [layerId]: error }));

    // Hide layer if there's an error
    if (error) {
      setLayerVisibility(prev => ({ ...prev, [layerId]: false }));
    }
  }, []);

  const toggleLayerVisibility = useCallback(layerId => {
    setLayerVisibility(prev => ({ ...prev, [layerId]: !prev[layerId] }));
  }, []);

  const updateLayerOpacity = useCallback(
    (layerId, opacity) => {
      dispatch(setLayerOpacity({ [layerId]: opacity }));
    },
    [dispatch],
  );

  const clearAllLayerVisibility = useCallback(() => {
    setLayerVisibility(prev => {
      return Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
    });
  }, []);

  const setLayerVisibilityBatch = useCallback(visibilityUpdates => {
    setLayerVisibility(prev => ({ ...prev, ...visibilityUpdates }));
  }, []);

  const removeLayer = useCallback(layerId => {
    setLayers(prev => {
      const newLayers = { ...prev };
      delete newLayers[layerId];
      return newLayers;
    });
    setLayerVisibility(prev => {
      const newVisibility = { ...prev };
      delete newVisibility[layerId];
      return newVisibility;
    });
    setLoadingStates(prev => {
      const newLoading = { ...prev };
      delete newLoading[layerId];
      return newLoading;
    });
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[layerId];
      return newErrors;
    });
  }, []);

  useEffect(() => {
    dispatch(setLayerVis(layerVisibility));
  }, [layerVisibility, dispatch]);

  return {
    layers,
    layerVisibility,
    layerOpacity: layerOpacityInfo,
    loadingStates,
    errors,
    updateLayer,
    setLayerLoading,
    setLayerError,
    toggleLayerVisibility,
    updateLayerOpacity,
    setLayerVisibility: setLayerVisibilityBatch,
    removeLayer,
    clearAllLayerVisibility,
  };
};
